/**
 * The four headline tiles' figures: this cycle, all time, the sparkline, and
 * the change against the previous billing cycle.
 */
import { percentChange } from './billing'

/**
 * The previous billing cycle cut at the same point of the cycle, exactly
 * (summary.previous_cycle), or null when there is none: the first cycle after a
 * Marketplace purchase has nothing to compare with, and "no comparison" is the
 * honest reading of that, not a change against zero.
 *
 * It replaces scaling the previous *calendar* month by the days elapsed, which
 * mixed a calendar-month bucket with a billing cycle that need not start on the
 * 1st.
 */
export function previousCycleOf(summary) {
  return summary?.previous_cycle?.available === true ? summary.previous_cycle : null
}

/**
 * @param summary   /billing/summary
 * @param history   /billing/usage over whole calendar months (monthly buckets)
 * @param monthKeys the calendar months the sparkline covers, oldest first ("2026-05" ...)
 * @param keys      the metric keys to build (pages_processed, ...)
 */
export function buildTileMetrics({ summary, history, monthKeys, keys }) {
  const current = summary?.current_month || {}
  const allTime = summary?.all_time || {}
  const previous = previousCycleOf(summary)
  // One bucket per calendar month, zero for a month with no usage. The last one
  // is this month straight from the ledger: it is not overwritten with
  // `current_month`, which is the billing cycle and need not be the same window.
  const byMonth = new Map((history?.monthly_breakdown || []).map(b => [b.month, b]))

  return keys.reduce((acc, key) => {
    const now = current[key] || 0
    const before = previous ? previous[key] || 0 : null
    acc[key] = {
      current: now,
      allTime: allTime[key] || 0,
      history: history ? monthKeys.map(m => byMonth.get(m)?.[key] || 0) : [],
      // Nothing then and nothing now is flat; there is no percentage of zero.
      change: before === 0 && now === 0 ? 0 : before === null ? null : percentChange(now, before),
      // A previous billing month that had none of this, and this one has some:
      // there was a prior period, so "no prior period" would be untrue.
      isNew: before === 0 && now > 0,
    }
    return acc
  }, {})
}
